import _ from 'underscore';
import { useState, useEffect, useCallback, useContext } from 'react';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import {
  fetchAllCampaignsData,
  fetchAdGroupsData,
  type IFetchAdGroupsDataProps,
  type IFetchCampaignsGridDataProps,
} from '@bingads-webui-react/copilot-troubleshooting-data';
import { adGroupDataParams as params, CampaignTypes } from './entity-selection-utils';
import { IAttachmentItem } from './types';

interface ICampaignGridItem {
  Id?: number | string;
  Name?: string;
  CampaignType?: keyof typeof CampaignTypes | string;
  Status?: string;
}

interface IAdGroupGridItem {
  Id?: number | string;
  Name?: string;
  EntityStatus?: string;
  Campaign?: {
    Name?: string;
  };
}

type EntitySelectionDataRouteProps = Pick<IFetchCampaignsGridDataProps & IFetchAdGroupsDataProps, 'accountId' | 'customerId' | 'ccToken'>;

export type UseEntitySelectionDataProps = EntitySelectionDataRouteProps & {
  perfMarker?: { willFetchData: () => void; willRender: () => void };
  logger?: { trace: (message: string) => void };
}

export const useEntitySelectionData = ({
  accountId,
  customerId,
  ccToken,
  perfMarker,
  logger,
}: UseEntitySelectionDataProps) => {
  const [campaigns, setCampaigns] = useState<IAttachmentItem[]>([]);
  const [adGroups, setAdGroups] = useState<IAttachmentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);
  const i18n = useContext(I18nContext) as I18nModel;

  const fetchData = useCallback(() => {
    perfMarker?.willFetchData();
    setIsLoading(true);
    setError(null);

    const args = {
      accountId,
      customerId,
      ccToken,
    };
    Promise.all([
      fetchAllCampaignsData(args),
      fetchAdGroupsData({ ...args, params, ccToken }),
    ])
      .then(([campaignsRes, adGroupsRes]) => {
        const mappedCampaigns = _.map(campaignsRes.data.value, (campaign: ICampaignGridItem) => ({
          id: campaign?.Id?.toString() || '',
          name: campaign?.Name || '',
          primaryAttribute: campaign?.CampaignType && campaign.CampaignType in CampaignTypes
            ? i18n.getString(CampaignTypes[campaign.CampaignType as keyof typeof CampaignTypes])
            : campaign?.CampaignType,
          status: campaign?.Status || '',
          category: 'Campaign',
        }));

        const mappedAdGroups = _.map(adGroupsRes.data.value, (adGroup: IAdGroupGridItem) => ({
          id: adGroup?.Id?.toString() || '',
          name: adGroup?.Name || '',
          primaryAttribute: adGroup?.Campaign?.Name || '',
          status: adGroup?.EntityStatus || '',
          category: 'AdGroup',
        }));

        setCampaigns(mappedCampaigns);
        setAdGroups(mappedAdGroups);
      }).catch((err) => {
        setError(err);
        logger?.trace(`[EntitySelection] Error fetching campaigns or ad groups data. Error: ${JSON.stringify(err)}`);
      }).finally(() => {
        setIsLoading(false);
        perfMarker?.willRender();
      });
  }, [perfMarker, accountId, customerId, ccToken, i18n, logger]);

  useEffect(() => {
    if (!accountId || !customerId || !ccToken) {
      return;
    }
    fetchData();
  }, [accountId, ccToken, customerId, fetchData]);

  return {
    campaigns,
    adGroups,
    all: [...campaigns, ...adGroups],
    isLoading,
    error,
    refetch: fetchData,
  };
};
